import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { Box, Radio, useMediaQuery, useTheme } from '@mui/material';
import NoDataFound from './NoDataFound';
import CustomToolbar from './CustomToolbar';
import { style } from './ormDataGridStyle';

const ORMDataGridV2 = forwardRef(
  (
    {
      rows,
      columns,
      pinnedColumns,
      //pinnedColumns should be in following format
      // {
      //   left?: string[]; // Optional field names to pin to the left
      //   right?: string[]; // Optional field names to pin to the right
      // }
      columnVisibilityModel, // provide object of hidden column
      uniqueRowId = '_id',
      onRowSelectionChange,
      onRowClick,
      checkboxSelection = false,
      handleSortModelChange,
      disableMultipleRowSelection = false,
      selectionMode = 'multi', // multi | single | none
      defaultPagination = { pageSize: 50, page: 0 },
      pageSizeOptions = [50, 100, 150],
      height = '800px',
      variant,
      initalRowSelectionModel,
      defaultRowSelectionModel = {
        type: 'include',
        ids: new Set(initalRowSelectionModel || []),
      },
      children,
      toolbarOptions = ['COLUMNS', 'FILTER', 'DENSITY', 'EXPORT', 'SEARCH'],
      isDefaultCompact = false,
      fileName,
      autoHeight = false,
      //Currently only Tag generator page is using server side filtering
      filterMode = 'client', // Default to client-side filtering
      onQuickFilterChange, // Function to handle quick filter change
      csvOptions,
      isRowSelectable,
      keepNonExistentRowsSelected = true,
      showToolbar = true,
      detailPanelExpandedRowIds = [],
      onDetailPanelExpandedRowIdsChange,
      disableRowSelectionOnClick = true,
      density: densityProp,
      getRowHeight: getRowHeightProp,
      // Keep each header title on a single line, truncated with an ellipsis, instead of wrapping.
      singleLineHeader = false,
      // Initial sort, applied once. Use this instead of DataGrid's `sortModel` when you
      // only want a default order: `sortModel` is a CONTROLLED prop, so passing it
      // without an `onSortModelChange` re-applies the sort on every render (MUI runs an
      // effect keyed on the prop's identity) and wipes interactive state such as the
      // quick-filter search text. Merged into `initialState` so it stays uncontrolled.
      defaultSortModel,
      ...rest
    },
    reference
  ) => {
    const safeRows = Array.isArray(rows) ? rows : [];
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [rowSelectionModel, setRowSelectionModel] = useState(defaultRowSelectionModel);
    const [paginationModel, setPaginationModel] = useState(defaultPagination);
    const [isCompact, setIsCompact] = useState(() => isDefaultCompact || densityProp === 'compact');
    const [quickFilterValue, setQuickFilterValue] = useState('');
    const rowsById = useMemo(() => new Map(safeRows.map(row => [row[uniqueRowId], row])), [safeRows, uniqueRowId]);

    const resolvedDensity = isCompact ? 'compact' : densityProp;

    const resolvedGetRowHeight = getRowHeightProp ?? (() => (isCompact ? 32 : 'auto'));

    const applyCompactRowMinHeight = isCompact && !getRowHeightProp;

    const handleQuickFilterChange = useCallback(
      event => {
        const value = event.target.value;
        setQuickFilterValue(value);
        onQuickFilterChange && onQuickFilterChange(event); // Trigger server-side filtering
      },
      [onQuickFilterChange]
    );

    const handleRowSelectionChange = useCallback(
      newRowSelectionModel => {
        const selectedIds = newRowSelectionModel.ids ? Array.from(newRowSelectionModel.ids) : [];
        setRowSelectionModel(prev => ({ ...prev, ids: new Set(selectedIds) }));

        if (onRowSelectionChange) {
          const selectedRows = selectedIds.map(selectedId => rowsById.get(selectedId)).filter(row => row !== undefined); // Filter out undefined rows (deleted rows)

          onRowSelectionChange(selectedRows, {
            selectedIds,
            currentRows: safeRows,
            uniqueRowId,
          });
        }
      },
      [onRowSelectionChange, rowsById, safeRows, uniqueRowId]
    );

    useImperativeHandle(
      reference,
      () => ({
        removeRowSelectionModel() {
          setRowSelectionModel(defaultRowSelectionModel);
        },
      }),
      [defaultRowSelectionModel]
    );

    const handleOnRowClick = useCallback(
      e => {
        if (onRowClick) onRowClick(e);
      },
      [onRowClick]
    );

    const onSortModelChange = useCallback(
      data => {
        if (handleSortModelChange) {
          handleSortModelChange(data);
        }
      },
      [handleSortModelChange]
    );

    const cellClassWithPointer = 'cell-pointer';
    const isSingleSelection = selectionMode === 'single';
    const isSelectionEnabled = selectionMode !== 'none';
    const resolvedHeight = isMobile && height === '800px' ? '70vh' : height;
    //If data is not found then use 300px height to show no data found icon
    const tableHeight = safeRows.length > 0 ? resolvedHeight : isMobile ? '280px' : '350px';
    // `height='auto'` and the `autoHeight` flag mean the same thing to a caller, and
    // 40+ call sites pass the former, so both are honoured here. Without this the
    // wrapper collapsed to content height while still being a scroll container, and
    // the grid's fractional pixel sizing rounded up past it — so every such table
    // scrolled a few pixels even when a single row fit. Implemented as a flex column
    // rather than the grid's `autoHeight` prop, which is deprecated in v8 in favour of
    // exactly that. Empty grids keep the fixed no-data height for the overlay.
    const useAutoHeight = (autoHeight || height === 'auto') && safeRows.length > 0;

    const handleMenuClick = useCallback(toggle => {
      setIsCompact(toggle);
    }, []);

    const detailPanelExpandedRowIdSet = useMemo(() => new Set(detailPanelExpandedRowIds), [detailPanelExpandedRowIds]);

    const gridSlots = useMemo(
      () => ({
        ...(isSingleSelection ? { baseCheckbox: Radio } : null),
        toolbar: toolbarOptions.length > 0 ? CustomToolbar : undefined,
        noRowsOverlay: NoDataFound,
      }),
      [isSingleSelection, toolbarOptions.length]
    );

    const resolvedCsvOptions = useMemo(() => {
      if (!csvOptions && !fileName) return csvOptions;
      return {
        ...csvOptions,
        fileName: csvOptions?.fileName || fileName,
      };
    }, [csvOptions, fileName]);

    const gridSlotProps = useMemo(
      () => ({
        loadingOverlay: {
          variant: 'circular-progress',
          noRowsVariant: 'circular-progress',
        },
        toolbar: {
          quickFilterValue,
          onQuickFilterChange: handleQuickFilterChange,
          toolbarOptions,
          handleMenuClick,
          filterMode,
          children,
          csvOptions: resolvedCsvOptions,
        },
      }),
      [
        quickFilterValue,
        handleQuickFilterChange,
        toolbarOptions,
        handleMenuClick,
        filterMode,
        children,
        resolvedCsvOptions,
      ]
    );

    const gridSx = useMemo(
      () => ({
        ...style(variant),
        // Auto-height mode: the grid is the flex child that reports its own content
        // height. `minHeight: 0` stops a flex child refusing to shrink below its
        // content, which is what reintroduces stray scrollable pixels.
        ...(useAutoHeight && { flex: '1 1 auto', minHeight: 0 }),
        ...(isSingleSelection && {
          '& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-checkboxInput': {
            display: 'none',
          },
        }),
        ...(applyCompactRowMinHeight && {
          '& .MuiDataGrid-row': {
            minHeight: '32px !important',
          },
          // Beats ormDataGridStyle's 40px rule. Scoped to `.MuiDataGrid-row` because MUI
          // puts `row--borderBottom` on the column header row too, and an unscoped
          // selector resizes the header along with the data rows.
          '& .MuiDataGrid-row.MuiDataGrid-row--borderBottom': {
            minHeight: '32px !important',
          },
        }),
        ...(singleLineHeader && {
          // Selector repeats `.MuiDataGrid-root` to outrank ormDataGridStyle’s `break-spaces` rule,
          // which otherwise wins and wraps long titles onto several lines. A plain `&.MuiDataGrid-root`
          // key cannot be used here: it would collide with the one spread in from `style(variant)`.
          '&.MuiDataGrid-root.MuiDataGrid-root .MuiDataGrid-columnHeaderTitle': {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
        }),
      }),
      [variant, isSingleSelection, applyCompactRowMinHeight, singleLineHeader, useAutoHeight]
    );

    const gridInitialState = useMemo(
      () => ({
        pinnedColumns: pinnedColumns,
        columns: { columnVisibilityModel },
        ...(defaultSortModel?.length && { sorting: { sortModel: defaultSortModel } }),
      }),
      [pinnedColumns, columnVisibilityModel, defaultSortModel]
    );

    const getRowId = useCallback(row => row[uniqueRowId], [uniqueRowId]);

    const getCellClassName = useCallback(params => {
      return params.field === 'name' ? cellClassWithPointer : '';
    }, []);

    return (
      <Box
        sx={{
          width: '100%',
          overflowX: 'auto',
          ...(useAutoHeight
            ? // Flex column is MUI's documented auto-height setup: the grid measures its
              // own content instead of stretching to fill a parent, and `overflowY:
              // visible` keeps this wrapper from becoming a scroll container itself.
              { display: 'flex', flexDirection: 'column', minHeight: 0, overflowY: 'visible' }
            : { height: tableHeight, overflowY: 'auto' }),
        }}
      >
        <DataGridPro
          rows={safeRows}
          columns={columns}
          filterMode={filterMode}
          getRowId={getRowId}
          checkboxSelection={isSelectionEnabled ? (isSingleSelection ? true : checkboxSelection) : false}
          disableRowSelectionOnClick={disableRowSelectionOnClick}
          keepNonExistentRowsSelected={keepNonExistentRowsSelected}
          isRowSelectable={isRowSelectable ?? (() => true)}
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={handleRowSelectionChange}
          detailPanelExpandedRowIds={detailPanelExpandedRowIdSet}
          onDetailPanelExpandedRowIdsChange={onDetailPanelExpandedRowIdsChange}
          slots={gridSlots}
          slotProps={gridSlotProps}
          showToolbar={showToolbar}
          disableAutosize={safeRows.length > 5 ? true : false}
          onRowClick={handleOnRowClick}
          cellClassName={getCellClassName}
          sx={gridSx}
          onSortModelChange={onSortModelChange}
          disableMultipleRowSelection={isSingleSelection ? true : disableMultipleRowSelection}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={pageSizeOptions}
          initialState={gridInitialState}
          {...rest}
          density={resolvedDensity}
          getRowHeight={resolvedGetRowHeight}
        />
      </Box>
    );
  }
);

export default ORMDataGridV2;
